/**
 * Two-Factor Authentication Service
 * Handles TOTP enrollment, verification, and management
 */

import apiDefaults from './api';

export interface TwoFactorEnrollResponse {
  success: boolean;
  data?: {
    secret: string;
    otpauth: string;
    qr: string;
    backupCodes: string[];
    message: string;
  };
  error?: string;
  message?: string;
}

export interface TwoFactorVerifyResponse {
  success: boolean;
  data?: {
    message: string;
    backupCodes?: string[];
  };
  error?: string;
  message?: string;
}

export interface TwoFactorStatusResponse {
  success: boolean;
  data?: {
    totpEnabled: boolean;
    webauthnEnabled: boolean;
    backupCodesRemaining: number;
    lastUsed?: string;
  };
  error?: string;
}

class TwoFactorService {
  /**
   * Start TOTP enrollment - generates QR code and secret
   */
  async enrollTotp(): Promise<TwoFactorEnrollResponse> {
    try {
      const response = await apiDefaults.post('/auth/2fa/totp/enroll');
      return response as TwoFactorEnrollResponse;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to start 2FA enrollment',
        message: error.response?.data?.message || 'Failed to start 2FA enrollment'
      };
    }
  }

  /**
   * Verify TOTP code and enable 2FA
   */
  async verifyTotp(otp: string): Promise<TwoFactorVerifyResponse> {
    try {
      const response = await apiDefaults.post('/auth/2fa/totp/verify', { otp });
      return response as TwoFactorVerifyResponse;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Invalid verification code',
        message: error.response?.data?.message || 'Invalid verification code'
      };
    }
  }

  /**
   * Disable TOTP 2FA
   */
  async disableTotp(otp: string): Promise<TwoFactorVerifyResponse> {
    try {
      const response = await apiDefaults.post('/auth/2fa/totp/disable', { otp });
      return response as TwoFactorVerifyResponse;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to disable 2FA',
        message: error.response?.data?.message || 'Failed to disable 2FA'
      };
    }
  }

  /**
   * Get 2FA status for current user
   */
  async getStatus(): Promise<TwoFactorStatusResponse> {
    try {
      const response = await apiDefaults.get('/auth/2fa/status');
      return response as TwoFactorStatusResponse;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get 2FA status'
      };
    }
  }

  /**
   * Regenerate backup codes
   */
  async regenerateBackupCodes(otp: string): Promise<TwoFactorVerifyResponse> {
    try {
      const response = await apiDefaults.post('/auth/2fa/backup-codes/regenerate', { otp });
      return response as TwoFactorVerifyResponse;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to regenerate backup codes',
        message: error.response?.data?.message || 'Failed to regenerate backup codes'
      };
    }
  }
}

export const twoFactorService = new TwoFactorService();
export default twoFactorService;
